import { defineStore } from 'pinia';
import api from '@/api';

export const useComponentStore = defineStore('component', {
  state: () => ({
    // List state with pagination
    components: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 50,
    totalPages: 0,

    // Single component
    component: null,

    // Search results
    searchResults: [],

    // Lookups for dropdowns
    lookups: [],

    // Components needing reorder
    reorderComponents: [],

    // Code availability check
    codeAvailability: null,

    // Batch import result
    batchImportResult: null,

    // Loading states
    loading: false,
    searchLoading: false,

    // Error state
    error: null
  }),

  getters: {
    getComponents: (state) => state.components,
    getComponent: (state) => state.component,
    getSearchResults: (state) => state.searchResults,
    getLookups: (state) => state.lookups,
    getReorderComponents: (state) => state.reorderComponents,
    getCodeAvailability: (state) => state.codeAvailability,
    getBatchImportResult: (state) => state.batchImportResult,
    getPagination: (state) => ({
      totalCount: state.totalCount,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      totalPages: state.totalPages
    }),
    isLoading: (state) => state.loading,
    isSearchLoading: (state) => state.searchLoading,
    getError: (state) => state.error
  },

  actions: {
    /**
     * Fetch components with pagination
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive]
     * @param {number} [params.pageNumber]
     * @param {number} [params.pageSize]
     */
    async fetchComponents(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.component.getComponents(params);
        this.components = resp.data.components || [];
        this.totalCount = resp.data.totalCount;
        this.pageNumber = resp.data.pageNumber;
        this.pageSize = resp.data.pageSize;
        this.totalPages = resp.data.totalPages;
      } catch (e) {
        this.error = e;
        this.components = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch single component by ID
     * @param {number} id
     */
    async fetchComponent(id) {
      this.loading = true;
      this.error = null;
      // Clear before the request settles, not just on error - otherwise a
      // detail view watching route.params.id briefly re-renders the
      // *previous* entity while the new one is still in flight.
      this.component = null;
      try {
        const resp = await api.component.getComponent(id);
        this.component = resp.data;
      } catch (e) {
        this.error = e;
        this.component = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch component by code
     * @param {string} code
     */
    async fetchComponentByCode(code) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.component.getComponentByCode(code);
        this.component = resp.data;
      } catch (e) {
        this.error = e;
        this.component = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch component by UPC barcode
     * @param {string} upc
     */
    async fetchComponentByUpc(upc) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.component.getComponentByUpc(upc);
        this.component = resp.data;
      } catch (e) {
        this.error = e;
        this.component = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Search components by name or code
     * @param {Object} [params]
     * @param {string} [params.query]
     * @param {number} [params.maxResults]
     */
    async searchComponents(params = {}) {
      this.searchLoading = true;
      this.error = null;
      try {
        const resp = await api.component.searchComponents(params);
        this.searchResults = resp.data || [];
      } catch (e) {
        this.error = e;
        this.searchResults = [];
        throw e;
      } finally {
        this.searchLoading = false;
      }
    },

    /**
     * Fetch component lookups for dropdowns
     * @param {Object} [params]
     * @param {number} [params.componentTypeId]
     */
    /**
     * Get a suggested, currently-unused component code
     */
    async fetchNextCode() {
      const resp = await api.component.getNextCode();
      return resp.data.componentCode;
    },

    /**
     * Copy a component into a new one
     * @param {number} id
     * @param {Object} [params]
     */
    async copyComponent(id, params = {}) {
      const resp = await api.component.copyComponent(id, params);
      return resp.data;
    },

    async fetchLookups(params = {}) {
      this.error = null;
      try {
        const resp = await api.component.getComponentLookups(params);
        this.lookups = resp.data || [];
      } catch (e) {
        this.error = e;
        this.lookups = [];
        throw e;
      }
    },

    /**
     * Fetch components needing reorder
     */
    async fetchReorderComponents() {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.component.getComponentsNeedReorder();
        this.reorderComponents = resp.data || [];
      } catch (e) {
        this.error = e;
        this.reorderComponents = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Check if component code is available
     * @param {string} code
     * @param {Object} [params]
     * @param {number} [params.excludeId]
     */
    async checkCodeAvailability(code, params = {}) {
      this.error = null;
      try {
        const resp = await api.component.checkCodeAvailability(code, params);
        this.codeAvailability = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        this.codeAvailability = null;
        throw e;
      }
    },

    /**
     * Create new component
     * @param {Object} payload
     */
    async createComponent(payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.component.createComponent(payload);
        this.component = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update component
     * @param {number} id
     * @param {Object} payload
     */
    async updateComponent(id, payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.component.updateComponent(id, payload);
        this.component = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Delete component (soft delete)
     * @param {number} id
     */
    async deleteComponent(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.component.deleteComponent(id);
        this.component = null;
        // Remove from list if present
        this.components = this.components.filter(c => c.componentId !== id);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Batch import components
     * @param {Object[]} components
     */
    async batchImport(components) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.component.batchImport(components);
        this.batchImportResult = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        this.batchImportResult = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // Clear methods
    clearComponents() {
      this.components = [];
      this.totalCount = 0;
      this.pageNumber = 1;
      this.totalPages = 0;
    },

    clearComponent() {
      this.component = null;
    },

    clearSearchResults() {
      this.searchResults = [];
    },

    clearLookups() {
      this.lookups = [];
    },

    clearReorderComponents() {
      this.reorderComponents = [];
    },

    clearCodeAvailability() {
      this.codeAvailability = null;
    },

    clearBatchImportResult() {
      this.batchImportResult = null;
    },

    clearError() {
      this.error = null;
    },

    clearAll() {
      this.clearComponents();
      this.clearComponent();
      this.clearSearchResults();
      this.clearLookups();
      this.clearReorderComponents();
      this.clearCodeAvailability();
      this.clearBatchImportResult();
      this.clearError();
    }
  }
});
